// frontend/src/ChatHistorySidebar.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ChatHistorySidebar.css';

const ChatHistorySidebar = ({ history, onChatSelect, activeChatId, refreshHistory }) => {
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedChats, setSelectedChats] = useState(new Set());

    useEffect(() => {
        setSelectedChats(new Set());
    }, [history]);

    const handleToggleDeleteMode = () => {
        if (isDeleteMode) {
            setSelectedChats(new Set());
        }
        setIsDeleteMode(!isDeleteMode);
    };

    const handleSelectChatForDeletion = (chatId) => {
        const newSelectedChats = new Set(selectedChats);
        if (newSelectedChats.has(chatId)) {
            newSelectedChats.delete(chatId);
        } else {
            newSelectedChats.add(chatId);
        }
        setSelectedChats(newSelectedChats);
    };

    const handleDeleteSelected = async () => {
        const chatIdsToDelete = Array.from(selectedChats);
        if (chatIdsToDelete.length === 0) {
            alert("Please select at least one chat to delete.");
            return;
        }

        const isConfirmed = window.confirm(`Are you sure you want to delete ${chatIdsToDelete.length} chat(s)? This action cannot be undone.`);
        if (!isConfirmed) {
            return;
        }

        try {
            const response = await axios.post('/api/delete-chats', 
                { chat_ids: chatIdsToDelete },
                { withCredentials: true }
            );

            if (response.status !== 200) {
                throw new Error(response.data.error || 'Failed to delete chats.');
            }
            
            if (activeChatId && selectedChats.has(activeChatId)) {
                onChatSelect(null);
            }
            
            refreshHistory();
            setIsDeleteMode(false);
            
        } catch (error) {
            console.error("Error deleting chats:", error);
            alert("An error occurred while deleting chats. Please try again.");
        }
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    return (
        <div className="chat-history-sidebar">
            <div className="sidebar-header">
                <h4>Chat History</h4>
                <button onClick={handleToggleDeleteMode} className="delete-mode-toggle" title={isDeleteMode ? "Cancel Deletion" : "Select chats to delete"}>
                    {isDeleteMode ? (
                        <i className="fas fa-times"></i>
                    ) : (
                        <img src={`${process.env.PUBLIC_URL}/trashbin.png`} alt="Toggle Delete Mode" />
                    )}
                </button>
            </div>
            <div className="chat-history-list">
                {!history ? (
                     <div className="sidebar-loading">Loading...</div>
                ) : history.length === 0 ? (
                    <div className="empty-history">No chat history.</div>
                ) : (
                    history.map((chat) => (
                        <div 
                            key={chat.id}
                            className={`chat-history-item ${activeChatId === chat.id && !isDeleteMode ? 'selected' : ''} ${selectedChats.has(chat.id) ? 'selected-for-deletion' : ''}`}
                            onClick={() => !isDeleteMode && onChatSelect(chat)}
                        >
                            {isDeleteMode && (
                                <input
                                    type="checkbox"
                                    className="chat-delete-checkbox"
                                    checked={selectedChats.has(chat.id)}
                                    onChange={() => handleSelectChatForDeletion(chat.id)}
                                    onClick={(e) => e.stopPropagation()} 
                                />
                            )}
                            <div className="chat-item-details">
                                <p className="chat-item-query">{chat.user_query || 'Untitled Chat'}</p>
                                <p className="chat-item-date">{formatDate(chat.date)}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {isDeleteMode && (
                <div className="delete-actions-bar">
                    <button onClick={handleDeleteSelected} className="delete-button-confirm">
                        Delete ({selectedChats.size})
                    </button>
                    <button onClick={handleToggleDeleteMode} className="delete-button-cancel">
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

export default ChatHistorySidebar;